import { parsePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input';

import { showToast } from './toaster';

// Internal extensions (e.g. "1003") dial as-is; anything longer is treated as
// an external PSTN number and normalized/validated against the AU dial plan.
const EXTENSION_PATTERN = /^\d{3,6}$/;

/**
 * Resolves a raw dialpad string into a number safe to send in a SIP INVITE.
 * Catches the case where a user drops the AU trunk prefix (e.g. dials
 * "468488455" instead of "0468488455"), which FreeSWITCH/the carrier
 * rejects with 488 Not Acceptable Here / Q.850 cause 88 (INCOMPATIBLE_DESTINATION)
 * instead of a client-side error.
 */
export function resolveDialNumber(rawNumber, defaultCountry = 'AU') {
  const trimmed = (rawNumber || '').trim();

  if (!trimmed) {
    return { number: null, error: 'Enter a number to dial' };
  }

  // Vertical service codes (e.g. *67) and internal extensions dial as-is.
  if (/[*#]/.test(trimmed) || EXTENSION_PATTERN.test(trimmed)) {
    return { number: trimmed, error: null };
  }

  if (!isValidPhoneNumber(trimmed, defaultCountry)) {
    return { number: null, error: `"${trimmed}" is not a valid phone number` };
  }

  const normalized = parsePhoneNumber(trimmed, defaultCountry).formatNational().replace(/\D/g, '');

  return { number: normalized, error: null };
}

export function normalizeSession(sipSession, type = null) {
  const raw = sipSession.session || sipSession;
  const id = raw.id || raw._id || '';
  const direction =
    type ||
    raw.direction ||
    (raw.incoming !== undefined ? (raw.incoming ? 'incoming' : 'outgoing') : 'unknown');

  const state = raw.state || raw._state || raw.status || 'unknown';
  const status = mapStateToStatus(state);

  const uriUser =
    raw.remoteIdentity?.uri?.user ||
    raw.callerNumber ||
    raw.remoteIdentity?.uri?.toString?.() ||
    'Unknown';

  return {
    id,
    direction,
    state, // normalized e.g., 'ringing', 'established', 'terminated'
    remoteIdentity: {
      uri: {
        user: uriUser,
      },
    },
    callerName: raw.callerName || raw.remoteIdentity?.displayName || '',
    callerNumber: raw.callerNumber || uriUser,

    // 🔊 Media & State
    isOnHold: raw.isOnHold || false,
    isMuted: raw.isMuted || false,

    // 🕒 Duration
    startTime: raw.startTime || Date.now(), // set when call is accepted
    endTime: raw.endTime || null,

    // 🎯 Call Status Tracking
    incomingCallAccepted: raw.incomingCallAccepted || false,
    outgoingCallAccepted: raw.outgoingCallAccepted || false,

    // 🔀 Transfer & Conference
    isBeingTransferred: raw.isBeingTransferred || false,
    transferTargetId: raw.transferTargetId || null, // callId of the target (for attended)
    transferState: raw.transferState || null,
    isConference: raw.isConference || false,
    conferenceGroupId: raw.conferenceGroupId || null, // shared ID between all merged calls

    // 📞 Internal linking
    rawSession: raw,
  };
}

export function mapStateToStatus(state) {
  if (!state) return 'unknown';
  const lower = state.toLowerCase();
  if (lower.includes('established')) return 'established';
  if (lower.includes('ringing') || lower === 'ringing') return 'ringing';
  if (lower.includes('early')) return 'early';
  if (lower.includes('held')) return 'held';
  if (lower.includes('terminated') || lower === 'ended') return 'ended';
  return lower;
}

export const CALL_STATUS = Object.freeze({
  INITIAL: 'initial', // Session created
  CONNECTING: 'connecting', // Dialing or trying to connect
  RINGING: 'ringing', // Remote side is ringing
  ESTABLISHED: 'established', // Call accepted and connected
  TERMINATED: 'terminated', // Call ended
  FAILED: 'failed', // Failed to connect
  BUSY: 'busy', // Remote is busy
  REJECTED: 'rejected', // Call was rejected
  CANCELED: 'canceled', // Outgoing call canceled
  HOLD: 'hold', // Call is on hold
  MUTED: 'muted', // Local audio muted
  TRANSFERRING: 'transferring', // In process of transfer
  TRANSFERRED: 'transferred', // Call transferred
  EARLY: 'early',
  Active: 'active',
  'ON-CALL': 'on-call',
});

export const CALL_TYPE = Object.freeze({
  INCOMING: 'incoming',
  OUTGOING: 'outgoing',
});

// utils/tonePlayer.js

// utils/tonePlayer.js

export function playTone(type, audioElemId) {
  // console.log(`[DEBUG] playTone called with type=${type}, id=${audioElemId}`);

  const elem = document.getElementById(audioElemId);
  if (!elem) {
    console.warn(`[WARN] Audio element ${audioElemId} not found`);
    return;
  }

  stopTone(audioElemId);

  elem.src = `/media/${type}.mp3`;
  elem.loop = true;
  elem.load();
  elem.play().catch((err) => {
    console.error('Tone play error:', err);
  });
}

export function stopTone(audioElemId) {
  const elem = document.getElementById(audioElemId);
  if (!elem) return;

  elem.pause();
  elem.currentTime = 0;
  elem.removeAttribute('src'); // Clear any previously set tone
  elem.load();
}

export function handleCallRejectionTone(code, audioElemId, showUserMessage = true) {
  stopTone(audioElemId);

  let tone = 'failed';
  let message = 'Call failed';

  switch (code) {
    case 486: // Busy Here
    case 600: // Busy Everywhere
    case 603: // Decline
      tone = 'busy';
      message = 'The user is busy or declined the call';
      break;

    case 404:
      tone = 'failed';
      message = 'Number not found';
      break;

    case 408:
    case 480:
      tone = 'failed';
      message = 'User is unavailable or timed out';
      break;

    default:
      tone = 'failed';
      message = `Call failed with status ${code}`;
  }

  playTone(tone, audioElemId);

  if (showUserMessage && typeof showToast === 'function') {
    showToast(message, 'warn');
  }
}
